/**
 * PaymentMandateSetup Component
 *
 * Allows users to set up payment mandates for recurring payments.
 * Supports GoCardless (SEPA), Stripe (Card), and Manual modes.
 */

import React, { useState } from 'react';
import {
  Building2,
  CreditCard,
  FileText,
  Check,
  AlertCircle,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import {
  useInitiateGoCardlessMandate,
  useInitiateStripeMandate,
  useCreateManualMandate,
  useGoCardlessConfig,
} from '../hooks/usePaymentMandates';
import type { MandateProvider, PaymentMandate } from '../types/mandate.types';

interface PaymentMandateSetupProps {
  clientId: string;
  clientName?: string;
  onComplete: (mandate: PaymentMandate) => void;
  onCancel?: () => void;
}

const providerOptions: {
  value: MandateProvider;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'GOCARDLESS',
    label: 'Prelevement SEPA',
    description: 'Via GoCardless - Delai 3-5 jours ouvrés',
    icon: <Building2 className="w-6 h-6" />,
  },
  {
    value: 'STRIPE',
    label: 'Carte bancaire',
    description: 'Visa, Mastercard - Paiement instantané',
    icon: <CreditCard className="w-6 h-6" />,
  },
  {
    value: 'MANUAL',
    label: 'Paiement manuel',
    description: 'Virement, chèque - Envoi de facture',
    icon: <FileText className="w-6 h-6" />,
  },
];

export function PaymentMandateSetup({
  clientId,
  clientName,
  onComplete,
  onCancel,
}: PaymentMandateSetupProps) {
  const [selectedProvider, setSelectedProvider] = useState<MandateProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: goCardlessConfig } = useGoCardlessConfig();
  const initiateGoCardless = useInitiateGoCardlessMandate();
  const initiateStripe = useInitiateStripeMandate();
  const createManual = useCreateManualMandate();

  const isLoading =
    initiateGoCardless.isPending ||
    initiateStripe.isPending ||
    createManual.isPending;

  const handleSetup = async () => {
    if (!selectedProvider) return;

    setError(null);

    try {
      switch (selectedProvider) {
        case 'GOCARDLESS': {
          if (!goCardlessConfig?.enabled) {
            setError('GoCardless n\'est pas configuré. Contactez votre administrateur.');
            return;
          }

          const result = await initiateGoCardless.mutateAsync({
            clientId,
            successUrl: `${window.location.origin}/payments/mandates/gocardless/complete`,
            description: clientName
              ? `Autorisation de prelevement - ${clientName}`
              : 'Autorisation de prelevement SEPA',
          });

          // Redirect to GoCardless authorization page
          window.location.href = result.redirectUrl;
          break;
        }

        case 'STRIPE': {
          // Stripe requires @stripe/stripe-js to confirm the SetupIntent client-side.
          // The backend returns a clientSecret for Stripe Elements confirmation.
          // Until the Stripe Elements flow is wired up, inform the user clearly.
          setError(
            'Le paiement par carte bancaire n\'est pas encore disponible. ' +
            'Veuillez utiliser le prélèvement SEPA ou le paiement manuel.'
          );
          break;
        }

        case 'MANUAL': {
          const mandate = await createManual.mutateAsync({ clientId });
          onComplete(mandate);
          break;
        }
      }
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Mode de paiement récurrent
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Choisissez comment ce client règlera ses factures récurrentes
        </p>
      </div>

      {/* Provider selection */}
      <div className="space-y-3">
        {providerOptions.map((option) => {
          const isDisabled =
            option.value === 'GOCARDLESS' && !goCardlessConfig?.enabled;
          const isSelected = selectedProvider === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => !isDisabled && setSelectedProvider(option.value)}
              disabled={isDisabled}
              className={`
                w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200
                ${
                  isSelected
                    ? 'border-teal-600 bg-teal-50/50'
                    : isDisabled
                    ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              <div
                className={`
                  p-3 rounded-xl
                  ${isSelected ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-500'}
                `}
              >
                {option.icon}
              </div>

              <div className="flex-1 text-left">
                <p
                  className={`font-medium ${
                    isSelected ? 'text-teal-700' : 'text-gray-900'
                  }`}
                >
                  {option.label}
                </p>
                <p className="text-sm text-gray-500">{option.description}</p>
                {isDisabled && option.value === 'GOCARDLESS' && (
                  <p className="text-xs text-amber-600 mt-1">
                    Non configuré - Contactez votre administrateur
                  </p>
                )}
              </div>

              {isSelected && (
                <div className="p-1 bg-teal-600 rounded-full">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
        )}

        <button
          type="button"
          onClick={handleSetup}
          disabled={!selectedProvider || isLoading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Configuration...
            </>
          ) : selectedProvider === 'GOCARDLESS' ? (
            <>
              Configurer
              <ExternalLink className="w-4 h-4" />
            </>
          ) : (
            'Configurer'
          )}
        </button>
      </div>
    </div>
  );
}

export default PaymentMandateSetup;
